import React, { useCallback, useMemo } from 'react';

import { TopLevelSpacer } from '@atlaskit/side-nav-items/top-level-spacer';

import GlobalShortcuts from '@atlassian/global-shortcuts/ui';
import type { GlobalShortcutsProps } from '@atlassian/global-shortcuts/types';
import { useOrderedAppShortcuts } from '@atlassian/global-shortcuts/use-ordered-app-shortcuts';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

// In preperation for upcoming platform adoption of side navigation
// https://hello.jira.atlassian.cloud/browse/NAVX-908
import { useShortcutItemsConfig } from '../../PlatformNavigation/AppShortcuts/hooks/useShortcutItemsConfig.tsx';
import { useTeamsAppShortcut } from '../../PlatformNavigation/AppShortcuts/hooks/useTeamsAppShortcut.tsx';

type GlobalAppShortcutsProps = {
	orderedMenuConfig?: { menuId: string; visible: boolean }[];
	onUpdateGetFilteredProductLinks?: GlobalShortcutsProps['onUpdateGetFilteredProductLinks'];
	isInvitedAppShortcutsEnabled?: boolean;
};

export const GlobalAppShortcuts = ({
	orderedMenuConfig = [],
	onUpdateGetFilteredProductLinks,
	isInvitedAppShortcutsEnabled = false,
}: GlobalAppShortcutsProps): React.JSX.Element => {
	const { cloudId } = useSessionData();
	const { config: teamsShortcutConfig, component: teamsShortcutComponent } = useTeamsAppShortcut();

	const updateGetFilteredProductLinks = useCallback(
		(getFilteredProductLinks) => {
			if (onUpdateGetFilteredProductLinks) {
				onUpdateGetFilteredProductLinks(getFilteredProductLinks);
			}
		},
		[onUpdateGetFilteredProductLinks],
	);

	const shortcutItemsConfig = useShortcutItemsConfig({ teamsShortcutConfig });

	const shortcutItemsConfigReduced = useMemo(() => {
		if (!isInvitedAppShortcutsEnabled) {
			return shortcutItemsConfig;
		}

		const { jiraShortcut: _jiraShortcut, ...rest } = shortcutItemsConfig;

		return rest;
	}, [shortcutItemsConfig, isInvitedAppShortcutsEnabled]);

	// Sort shortcut items based on the ordered menu config
	const orderedShortcuts = useOrderedAppShortcuts(shortcutItemsConfigReduced, orderedMenuConfig);

	const globalShortcutsProps: GlobalShortcutsProps = {
		testId: 'global-app-shortcuts',
		cloudId,
		shortcutItems: orderedShortcuts,
		product: 'confluence' as const,
		onUpdateGetFilteredProductLinks: updateGetFilteredProductLinks,
	};

	return (
		<>
			<GlobalShortcuts {...globalShortcutsProps} />
			{orderedShortcuts.length > 0 && <TopLevelSpacer />}
			{teamsShortcutComponent}
		</>
	);
};
